<template>
  <client-only>
    <!-- Choose a legal root for where you mount this (div by default, li/td supported) -->
    <component :is="rootTag" class="last-searches-container" @click.stop>
      <div class="last-searches-dropdown">
        <!-- Render only after the first client tick to avoid brittle hydration -->
        <ul v-if="isReady" class="searches-list">
          <li v-if="recentSearches.length === 0" class="no-searches-message" role="status" aria-live="polite">
            No recent searches.
          </li>

          <li v-for="(search, index) in recentSearches" :key="searchKey(search, index)" class="search-item"
            @click="handleSelectSearch(search)">
            <span class="search-text">{{ search.readable }}</span>
            <button @click.stop="removeSearch(index)" class="remove-button">×</button>
          </li>
        </ul>
        <!-- Optional tiny placeholder to keep DOM stable before isReady -->
        <ul v-else class="searches-list">
          <li class="no-searches-message" role="status" aria-live="polite">Loading…</li>
        </ul>
      </div>
    </component>
  </client-only>
</template>

<script>
export default {
  name: "LastSearches",

  props: {
    /**
     * If you render inside a <ul>, pass as="li".
     * If inside a <tr>, pass as="td" or as="th".
     */
    as: { type: String, default: "div" },
  },

  data() {
    return {
      isReady: false,
      clientRecentSearches: [],
    };
  },

  computed: {
    rootTag() {
      const valid = ["div", "li", "td", "th", "span"];
      return valid.includes(this.as) ? this.as : "div";
    },
    recentSearches() {
      try {
        const storeData = this.$store?.state?.jobs?.recentSearches || [];
        if (Array.isArray(storeData) && storeData.length) return storeData;
        return Array.isArray(this.clientRecentSearches) ? this.clientRecentSearches : [];
      } catch {
        return [];
      }
    },
  },

  created() {
    try {
      if (process.client && !this.$store?.state?.jobs?.recentSearches?.length) {
        this.$store.dispatch("jobs/loadRecentSearches").catch(() => { });
      }
    } catch { }
  },

  mounted() {
    // Delay first paint one tick to avoid patching into an unstable spot
    this.$nextTick(() => {
      try {
        const filters = JSON.parse(localStorage.getItem("recentSearches") || "[]");
        const readableArr = JSON.parse(localStorage.getItem("recentSearchReadable") || "[]");
        this.clientRecentSearches = Array.isArray(filters)
          ? filters.map((filter, i) => ({ filter, readable: readableArr[i] || "" }))
          : [];
      } catch {
        this.clientRecentSearches = [];
      } finally {
        this.isReady = true;
      }
    });

    // Helpful runtime warnings about illegal placement
    try {
      const parentTag = this.$el?.parentNode?.tagName;
      if (parentTag === "UL" && this.rootTag !== "LI") {
        // eslint-disable-next-line no-console
        console.warn('[LastSearches] Inside <ul> but root is not <li>. Use <LastSearches as="li" />');
      }
      if (parentTag === "P" && (this.rootTag === "DIV" || this.rootTag === "UL" || this.rootTag === "LI")) {
        console.warn("[LastSearches] Parent is <p>. Replace it with a <div> or use as=\"span\".");
      }
      if (
        (parentTag === "TR" || parentTag === "THEAD" || parentTag === "TBODY" || parentTag === "TFOOT") &&
        !(this.rootTag === "TD" || this.rootTag === "TH")
      ) {
        console.warn('[LastSearches] Inside table row. Use <LastSearches as="td" /> or as="th".');
      }
    } catch { }
  },

  // Component-level error boundary so errors here don't kill the whole app
  errorCaptured(err, vm, info) {
    // eslint-disable-next-line no-console
    console.error("[LastSearches:errorCaptured]", err, info);
    // Render a safe empty state instead of crashing the app
    this.clientRecentSearches = [];
    this.isReady = true;
    // Returning false prevents the error from propagating to parent and global handler
    return false;
  },

  methods: {
    searchKey(search, index) {
      try {
        return search.readable || JSON.stringify(search.filter) || index;
      } catch {
        return index;
      }
    },
    removeSearch(index) {
      this.$store.commit("jobs/removeRecentSearch", index);
    },
    async handleSelectSearch(search) {
      if (!search || typeof search !== "object" || !search.filter) return;

      this.$store.commit("jobs/setFilter", {
        ...this.$store.state.jobs.filter,
        ...search.filter,
      });

      this.$emit("selectSearch");

      const loading = this.$nuxt && this.$nuxt.$loading;
      if (loading && loading.start) loading.start();

      await this.$store.dispatch("jobs/getJobs").catch(() => { });
      await this.$nextTick();

      if (loading && loading.finish) loading.finish();

      this.$emit("closePopup");
    },
  },
};
</script>

<style lang="scss" scoped>
.last-searches-container {
  position: absolute;
  left: 33%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 350px;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  padding: 0;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.12), 0 8px 25px rgba(0, 0, 0, 0.15);
}

.last-searches-dropdown {
  max-height: 600px;
  border-radius: 6px;
  padding: 15px;
}

.searches-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.search-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  margin-bottom: 6px;
  background: #fff;
  cursor: pointer;
  color: #808080;
  font-size: 16px;
  font-weight: 400;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  user-select: none;

  &:hover {
    background-color: rgba(42, 65, 232, 0.04);
    color: #2a41e8;
    border-radius: 6px;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.search-text {
  flex: 1;
  transition: color 0.2s;
}

.remove-button {
  color: #2a41e8;
  border: none;
  background: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  @media (min-width: 769px) {
    display: none;
  }
}

.no-searches-message {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 15px 0;
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .last-searches-container {
    width: 90%;
    left: 50%;
    max-width: 320px;
  }

  .remove-button {
    display: flex;
  }
}

@media (min-width: 769px) {
  .search-item:hover .remove-button {
    display: flex;
  }
}
</style>
